import React, { useState, useEffect } from 'react';
import {
  Package2,
  Search,
  MapPin,
  CalendarClock,
  Truck,
  CheckCircle2,
  Circle,
  Package,
  IndianRupee,
  Clock,
  User,
  Box,
  MapPinned,
  Calendar,
} from 'lucide-react';
import NavBar from '../../../component/b2b/navBar/b2bNavBar';
import Sidebar from '../../../component/b2b/sideBar/b2bSideBar';
import { useMyContext } from '../../../context/MyContext';
import '../../../css/custom-scrollbar.css';
import axios from 'axios';
import { Base_Url, b2bTrackShipment } from '../../../config/config';

const B2BTrackShipment = () => {
  const [waybillNumber, setWaybillNumber] = useState('');
  const [orderTrackingInfo, setOrderTrackingInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const { isDarkMode, isToggleSidebar, orderid, setorderId } = useMyContext();

  useEffect(() => {
    if (orderid) {
      handleTrack();
    }
  }, [orderid]);

  const handleTrack = async () => {
    const trackingNumber = orderid || waybillNumber;
    if (!trackingNumber) {
      setError('Please enter a valid order ID');
      setOrderTrackingInfo(null);
      return;
    }

    setIsLoading(true);
    setError('');
    const token = localStorage.getItem('token');
    const cus_id = localStorage.getItem('cus_id');

    try {
      const response = await axios.post(
        Base_Url + b2bTrackShipment,
        {
          order_id: trackingNumber,
          shipment_type: 'Forward',
        },
        {
          headers: {
            token: token,
            cus_id: cus_id,
            'Content-Type': 'application/json',
          },
        }
      );

      const shipmentData = response.data.data.ShipmentData[0]?.Shipment;
      setOrderTrackingInfo(shipmentData || null);
      setError('');
      setorderId(null);
    } catch (err) {
      setError('Failed to fetch tracking information. Please try again.');
      setOrderTrackingInfo(null);
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Not Available';
    return new Date(dateString).toLocaleString();
  };

  const DetailRow = ({ label, value, icon: Icon }) => (
    <div className="flex items-center justify-between py-2 border-b border-gray-100 dark:border-gray-700">
      <div className="flex items-center space-x-2">
        {Icon && <Icon className="w-4 h-4 text-gray-400" />}
        <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
      </div>
      <span className="text-sm font-medium dark:text-white">{value || 'Not Available'}</span>
    </div>
  );

  const getStepIcon = (scan, isLatest) => {
    const status = scan.status?.toLowerCase();
    if (status) {
      return <CheckCircle2 className="w-6 h-6 text-green-500" />;
    }
    if (isLatest) {
      return (
        <div className="relative inline-flex items-center justify-center w-8 h-8 rounded-full bg-purple-100 animate-pulse">
          <Truck className="w-4 h-4 text-purple-600" />
        </div>
      );
    }
    return <Circle className="w-6 h-6 text-gray-400" />;
  };

  const DeliveryInfoPanel = () => (
    <div className="space-y-6">
      {/* Shipment Overview Card */}
      <div className="bg-white dark:bg-darkmodeColor rounded-lg shadow-lg">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold dark:text-white flex items-center gap-2">
            <Package2 className="w-5 h-5 text-purple-600" /> Shipment Overview
          </h2>
        </div>
        <div className="p-4 space-y-3">
          <DetailRow label="AWB Number" value={orderTrackingInfo?.AWB} icon={Package} />
          <DetailRow label="Waybill no" value={orderTrackingInfo?.ReferenceNo} icon={Package2} />
          <DetailRow label="Order Type" value={orderTrackingInfo?.OrderType} icon={Box} />
          <DetailRow
            label="Invoice Amount"
            value={`₹${orderTrackingInfo?.InvoiceAmount || 0}`}
            icon={IndianRupee}
          />
          <DetailRow
            label="COD Amount"
            value={`₹${orderTrackingInfo?.CODAmount || 0}`}
            icon={IndianRupee}
          />
          <DetailRow label="Quantity" value={orderTrackingInfo?.Quantity} icon={Box} />
        </div>
      </div>

      {/* Location Details */}
      <div className="bg-white dark:bg-darkmodeColor rounded-lg shadow-lg">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold dark:text-white flex items-center gap-2">
            <MapPinned className="w-5 h-5 text-blue-600" /> Location Details
          </h2>
        </div>
        <div className="p-4 space-y-3">
          <DetailRow label="Origin" value={orderTrackingInfo?.Origin} icon={MapPin} />
          <DetailRow
            label="Destination"
            value={orderTrackingInfo?.Destination || 'Not specified'}
            icon={MapPin}
          />
          <DetailRow label="Pickup Location" value={orderTrackingInfo?.PickupLocation} icon={MapPin} />
        </div>
      </div>

      {/* Consignee Details */}
      <div className="bg-white dark:bg-darkmodeColor rounded-lg shadow-lg">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold dark:text-white flex items-center gap-2">
            <User className="w-5 h-5 text-green-600" /> Consignee Details
          </h2>
        </div>
        <div className="p-4 space-y-3">
          {orderTrackingInfo?.Consignee && (
            <>
              <DetailRow label="Name" value={orderTrackingInfo.Consignee.Name} icon={User} />
              <DetailRow
                label="City"
                value={orderTrackingInfo.Consignee.City || orderTrackingInfo.Consignee.State}
                icon={MapPin}
              />
              <DetailRow label="Pin Code" value={orderTrackingInfo.Consignee.PinCode} icon={MapPin} />
              <DetailRow label="Country" value={orderTrackingInfo.Consignee.Country} icon={MapPin} />
            </>
          )}
        </div>
      </div>

      {/* Dates Information */}
      <div className="bg-white dark:bg-darkmodeColor rounded-lg shadow-lg">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold dark:text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-orange-600" /> Important Dates
          </h2>
        </div>
        <div className="p-4 space-y-3">
          <DetailRow label="Pickup Date" value={formatDate(orderTrackingInfo?.PickUpDate)} icon={Calendar} />
          <DetailRow
            label="Expected Delivery"
            value={formatDate(orderTrackingInfo?.ExpectedDeliveryDate)}
            icon={Calendar}
          />
          <DetailRow
            label="First Attempt Date"
            value={formatDate(orderTrackingInfo?.FirstAttemptDate)}
            icon={Calendar}
          />
        </div>
      </div>

      {/* Status Banner */}
      <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg">
        <div className="flex items-center space-x-3 mb-2">
          <Clock className="w-5 h-5 text-purple-600" />
          <h3 className="font-medium dark:text-white">Last Scan</h3>
        </div>
        <div className="space-y-2">
          <p className="text-sm font-medium text-purple-600 dark:text-purple-400">
            {orderTrackingInfo?.Status?.StatusLocation}
          </p>
          {/* <p className="text-sm text-gray-600 dark:text-gray-300">
            {orderTrackingInfo?.Status?.Instructions}
          </p> */}
          <p className="text-sm text-gray-500">
            Last Updated: {formatDate(orderTrackingInfo?.Status?.StatusDateTime)}
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className={`h-screen flex flex-col overflow-hidden ${
        isDarkMode ? 'dark:bg-darkmodeColor' : 'bg-gray-50'
      }`}
    >
      <div className="z-10">
        <NavBar />
      </div>
      <div className="flex flex-1 overflow-hidden">
        {isToggleSidebar && (
          <div
            className="fixed z-20 h-full w-64 md:relative duration-300 bg-gray-200 dark:bg-darkmodeColor dark:text-white md:bg-transparent mt-4 md:w-64 md:z-20"
            style={
              window.innerWidth < 768
                ? { top: '113px', height: 'calc(100vh - 115px)', overflowY: 'auto' }
                : {}
            }
          >
            <Sidebar />
          </div>
        )}
        <div className="flex-1 p-4 md:p-8 bg-gray-100 overflow-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-5 max-w-7xl mx-auto">
            {/* Main Tracking Section */}
            <div className="lg:col-span-2 bg-white dark:bg-darkmodeColor shadow-lg rounded-lg">
              <div className="p-6">
                <div className="flex items-center space-x-2 mb-6">
                  <Package2 className="w-6 h-6 text-purple-600" />
                  <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Track Your Shipment
                  </h1>
                </div>
                <div className="space-y-4">
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      placeholder="Enter Order ID"
                      value={waybillNumber}
                      onChange={(e) => setWaybillNumber(e.target.value)}
                      className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 dark:bg-gray-800 dark:border-gray-700 dark:text-white"
                    />
                    <button
                      onClick={handleTrack}
                      disabled={isLoading}
                      className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 disabled:opacity-50 flex items-center space-x-2"
                    >
                      {isLoading ? (
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <Search className="w-4 h-4" />
                          <span>Track</span> 
                        </>
                      )}
                    </button>
                  </div>

                  {error && (
                    <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700 dark:bg-red-900/20 dark:text-red-400">
                      {error}
                    </div>
                  )}

                  {orderTrackingInfo && (
                    <div className="space-y-6 mt-8">
                      {orderTrackingInfo.scans?.map((scan, index) => (
                        <div key={index} className="relative">
                          <div className="flex items-start space-x-4">
                            <div className="flex-shrink-0">{getStepIcon(scan, index === 0)}</div>
                            <div className="flex-1 space-y-1">
                              <p className="font-medium dark:text-white">{scan.status}</p>
                              <p className="text-xs text-gray-400">{scan.scan_remark}</p>
                              {/* <p className="text-xs text-gray-400">{formatDate(scan.scan_timestamp)}</p> */}
                              {/* <p className="text-sm text-gray-500 dark:text-gray-400">{scan.location}</p> */}
                            </div>
                          </div>
                          {index < orderTrackingInfo.scans.length - 1 && (
                            <div className="absolute left-3 top-8 w-0.5 h-16 bg-gray-200 dark:bg-gray-700" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Info Panel */}
            <div className="lg:col-span-3">
              <DeliveryInfoPanel />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default B2BTrackShipment;



  // <div className="min-h-screen bg-gray-50 dark:bg-darkmodeColor p-4 md:p-8 flex items-center justify-center">
  //     <div className="w-full max-w-4xl animate-fadeIn">
  //       {/* Main Tracking Card */}
  //       <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl overflow-hidden transform transition-all duration-500 hover:scale-[1.01]">
  //         {/* Gradient Header */}
  //         <div className="relative bg-gradient-to-r from-purple-600 via-purple-500 to-indigo-600 p-6 md:p-8">
  //           <div className="absolute inset-0 bg-black opacity-5"></div>
  //           <div className="relative z-10">
  //             <div className="flex items-center justify-between mb-4">
  //               <div className="flex items-center space-x-3">
  //                 <div className="bg-white/20 backdrop-blur-sm p-3 rounded-xl">
  //                   <Package2 className="w-6 h-6 text-white" />
  //                 </div>
  //                 <div>
  //                   <p className="text-white/80 text-sm font-medium">Tracking Number</p>
  //                   <p className="text-white text-xl font-bold tracking-wide">SHIP123456789</p>
  //                 </div>
  //               </div>
  //               <div className="bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full">
  //                 <span className="text-white text-sm font-semibold">In Transit</span>
  //               </div>
  //             </div>

  //             {/* Status Progress Bar */}
  //             <div className="mt-6">
  //               <div className="flex items-center justify-between mb-2">
  //                 <span className="text-white/90 text-sm font-medium">Delivery Progress</span>
  //                 <span className="text-white/90 text-sm font-medium">75%</span>
  //               </div>
  //               <div className="w-full bg-white/20 rounded-full h-2 overflow-hidden">
  //                 <div className="bg-white h-full rounded-full transition-all duration-1000 ease-out animate-progressBar" style={{ width: '75%' }}></div>
  //               </div>
  //             </div>
  //           </div>
  //         </div>

  //         {/* Content Section */}
  //         <div className="p-6 md:p-8 space-y-8">
  //           {/* Current Status Banner */}
  //           <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-900/20 rounded-xl p-4 border border-purple-200/50 dark:border-purple-700/50 animate-slideUp">
  //             <div className="flex items-center space-x-3">
  //               <div className="relative">
  //                 <div className="absolute inset-0 bg-purple-600 rounded-full animate-ping opacity-75"></div>
  //                 <div className="relative bg-purple-600 p-2 rounded-full">
  //                   <Truck className="w-5 h-5 text-white" />
  //                 </div>
  //               </div>
  //               <div>
  //                 <p className="font-semibold text-gray-900 dark:text-white">Out for Delivery</p>
  //                 <p className="text-sm text-gray-600 dark:text-gray-400">Your package is on the way</p>
  //               </div>
  //             </div>
  //           </div>

  //           {/* Timeline Section */}
  //           <div className="space-y-6">
  //             <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
  //               <Clock className="w-5 h-5 mr-2 text-purple-600" />
  //               Tracking Timeline
  //             </h3>

  //             <div className="relative space-y-6 ml-3">
  //               {/* Vertical Line */}
  //               <div className="absolute left-[11px] top-0 bottom-0 w-0.5 bg-gradient-to-b from-purple-600 via-indigo-400 to-gray-300 dark:to-gray-700"></div>

  //               {/* Timeline Steps */}
  //               {[
  //                 { status: 'Delivered', date: 'Dec 15, 2024 - 02:30 PM', location: 'New Delhi, India', icon: CheckCircle2, completed: false, active: false },
  //                 { status: 'Out for Delivery', date: 'Dec 15, 2024 - 08:00 AM', location: 'New Delhi Hub', icon: Truck, completed: false, active: true },
  //                 { status: 'Shipped', date: 'Dec 14, 2024 - 03:15 PM', location: 'Mumbai Warehouse', icon: Package, completed: true, active: false },
  //                 { status: 'Packed', date: 'Dec 13, 2024 - 11:20 AM', location: 'Processing Center', icon: Box, completed: true, active: false },
  //                 { status: 'Order Placed', date: 'Dec 12, 2024 - 05:45 PM', location: 'Online', icon: CheckCircle2, completed: true, active: false }
  //               ].map((step, index) => (
  //                 <div
  //                   key={index}
  //                   className={`relative flex items-start space-x-4 transition-all duration-500 animate-slideUp hover:translate-x-1`}
  //                   style={{ animationDelay: `${index * 150}ms` }}
  //                 >
  //                   <div className="relative flex-shrink-0">
  //                     {step.active ? (
  //                       <div className="relative">
  //                         <div className="absolute inset-0 bg-purple-600 rounded-full animate-pulse"></div>
  //                         <div className="relative bg-purple-600 p-2 rounded-full shadow-lg ring-4 ring-purple-200 dark:ring-purple-900">
  //                           <step.icon className="w-5 h-5 text-white" />
  //                         </div>
  //                       </div>
  //                     ) : step.completed ? (
  //                       <div className="bg-green-500 p-2 rounded-full shadow-md">
  //                         <CheckCircle2 className="w-5 h-5 text-white" />
  //                       </div>
  //                     ) : (
  //                       <div className="bg-gray-300 dark:bg-gray-600 p-2 rounded-full">
  //                         <step.icon className="w-5 h-5 text-gray-500 dark:text-gray-400" />
  //                       </div>
  //                     )}
  //                   </div>

  //                   <div className="flex-1 pb-8">
  //                     <div className={`bg-white dark:bg-gray-700/50 rounded-lg p-4 shadow-sm border transition-all duration-300 hover:shadow-md ${step.active
  //                         ? 'border-purple-300 dark:border-purple-600 bg-purple-50/50 dark:bg-purple-900/20'
  //                         : step.completed
  //                           ? 'border-green-200 dark:border-green-800'
  //                           : 'border-gray-200 dark:border-gray-700'
  //                       }`}>
  //                       <div className="flex items-center justify-between mb-1">
  //                         <p className={`font-semibold ${step.active
  //                             ? 'text-purple-700 dark:text-purple-400'
  //                             : step.completed
  //                               ? 'text-gray-900 dark:text-white'
  //                               : 'text-gray-500 dark:text-gray-400'
  //                           }`}>
  //                           {step.status}
  //                         </p>
  //                         {step.active && (
  //                           <span className="px-2 py-1 bg-purple-600 text-white text-xs font-semibold rounded-full animate-pulse">
  //                             Current
  //                           </span>
  //                         )}
  //                       </div>
  //                       <p className="text-sm text-gray-600 dark:text-gray-400 flex items-center">
  //                         <Calendar className="w-3 h-3 mr-1" />
  //                         {step.date}
  //                       </p>
  //                       <p className="text-xs text-gray-500 dark:text-gray-500 flex items-center mt-1">
  //                         <MapPin className="w-3 h-3 mr-1" />
  //                         {step.location}
  //                       </p>
  //                     </div>
  //                   </div>
  //                 </div>
  //               ))}
  //             </div>
  //           </div>

  //           {/* Details Grid */}
  //           <div className="grid md:grid-cols-2 gap-6 pt-6 border-t border-gray-200 dark:border-gray-700">
  //             {/* Customer Details */}
  //             <div className="space-y-4 animate-slideUp" style={{ animationDelay: '600ms' }}>
  //               <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
  //                 <User className="w-5 h-5 mr-2 text-purple-600" />
  //                 Customer Details
  //               </h3>
  //               <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 space-y-3 transition-all duration-300 hover:shadow-md">
  //                 <div className="flex items-center space-x-3">
  //                   <div className="bg-purple-100 dark:bg-purple-900/50 p-2 rounded-lg">
  //                     <User className="w-4 h-4 text-purple-600 dark:text-purple-400" />
  //                   </div>
  //                   <div>
  //                     <p className="text-xs text-gray-500 dark:text-gray-400">Name</p>
  //                     <p className="font-medium text-gray-900 dark:text-white">Rahul Sharma</p>
  //                   </div>
  //                 </div>
  //                 <div className="flex items-center space-x-3">
  //                   <div className="bg-indigo-100 dark:bg-indigo-900/50 p-2 rounded-lg">
  //                     <Package className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
  //                   </div>
  //                   <div>
  //                     <p className="text-xs text-gray-500 dark:text-gray-400">Phone</p>
  //                     <p className="font-medium text-gray-900 dark:text-white">+91 98765 43210</p>
  //                   </div>
  //                 </div>
  //               </div>
  //             </div>

  //             {/* Delivery Address */}
  //             <div className="space-y-4 animate-slideUp" style={{ animationDelay: '700ms' }}>
  //               <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center">
  //                 <MapPin className="w-5 h-5 mr-2 text-purple-600" />
  //                 Delivery Address
  //               </h3>
  //               <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 transition-all duration-300 hover:shadow-md">
  //                 <div className="flex items-start space-x-3">
  //                   <div className="bg-purple-100 dark:bg-purple-900/50 p-2 rounded-lg flex-shrink-0">
  //                     <MapPinned className="w-4 h-4 text-purple-600 dark:text-purple-400" />
  //                   </div>
  //                   <div>
  //                     <p className="font-medium text-gray-900 dark:text-white mb-1">
  //                       123, Nehru Place, Block A
  //                     </p>
  //                     <p className="text-sm text-gray-600 dark:text-gray-400">
  //                       New Delhi, Delhi - 110019
  //                     </p>
  //                     <p className="text-sm text-gray-600 dark:text-gray-400">India</p>
  //                   </div>
  //                 </div>
  //               </div>
  //             </div>
  //           </div>

  //           {/* Footer Info */}
  //           <div className="flex items-center justify-between pt-6 border-t border-gray-200 dark:border-gray-700 animate-slideUp" style={{ animationDelay: '800ms' }}>
  //             <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
  //               <IndianRupee className="w-4 h-4" />
  //               <span>COD: ₹2,499</span>
  //             </div>
  //             <div className="flex items-center space-x-2 text-sm text-gray-600 dark:text-gray-400">
  //               <Box className="w-4 h-4" />
  //               <span>Weight: 2.5 kg</span>
  //             </div>
  //           </div>
  //         </div>
  //       </div>
  //     </div>

  //     <style jsx>{`
  //   @keyframes fadeIn {
  //     from {
  //       opacity: 0;
  //       transform: translateY(20px);
  //     }
  //     to {
  //       opacity: 1;
  //       transform: translateY(0);
  //     }
  //   }

  //   @keyframes slideUp {
  //     from {
  //       opacity: 0;
  //       transform: translateY(30px);
  //     }
  //     to {
  //       opacity: 1;
  //       transform: translateY(0);
  //     }
  //   }

  //   @keyframes progressBar {
  //     from {
  //       width: 0%;
  //     }
  //   }

  //   .animate-fadeIn {
  //     animation: fadeIn 0.6s ease-out;
  //   }

  //   .animate-slideUp {
  //     animation: slideUp 0.6s ease-out forwards;
  //     opacity: 0;
  //   }

  //   .animate-progressBar {
  //     animation: progressBar 1.5s ease-out;
  //   }
  // `}</style>
  //   </div>
